import React, {memo, useCallback} from "react";
import {Button, Icon, IconName, MaybeElement, MenuItem} from "@blueprintjs/core";
import {ItemPredicate, ItemRenderer, Select} from "@blueprintjs/select";
import {DEFAULT_POPOVER_MODIFIERS, EmptyValueStrings} from "../../../../constants/constants";
import {Pref, VehicleType} from "../../../../generated/graphql";
import VehicleIconResolver from "../../../vehicles/VehicleIconResolver";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {usePreferenceContext} from "../../../../providers/PreferenceProvider";
import {SizeProp} from "@fortawesome/fontawesome-svg-core";
import styled from "@emotion/styled";

type VehicleSelectorOptions = {
  showSelectedText?: boolean;
  iconSize?: SizeProp;
  dropdownIcon?: IconName | MaybeElement;
  dropdownIconSize?: number;
  scrollToActiveItem?: boolean;
};

export interface VehicleSelectorProps {
  items: VehicleType[];
  onItemSelect: (vehicle: VehicleType) => void;
  selectedItem: VehicleType | undefined | null;
  options?: VehicleSelectorOptions;
}

export const DefaultOptions: VehicleSelectorOptions = {
  dropdownIcon: "chevron-down",
  dropdownIconSize: 16,
  iconSize: "lg",
  showSelectedText: false,
  scrollToActiveItem: true
};

export const getVehicleIcon = (
  vehicleDescription: string | undefined,
  tenantPreferences: Pref[],
  size: SizeProp = "lg"
) => {
  return (
    <FontAwesomeIcon
      data-testid={"vehicle-icon"}
      size={size}
      color={"#4C505E"}
      icon={VehicleIconResolver(vehicleDescription, tenantPreferences)}
      title={vehicleDescription ?? EmptyValueStrings.unknown}
      style={{margin: "auto", width: "30px"}}
    />
  );
};

const filterItem: ItemPredicate<VehicleType> = (query, vehicle, _index, exactMatch) => {
  const normalizedDescription = vehicle.description?.toLowerCase() ?? "";
  const normalizedQuery = query.toLowerCase();

  if (exactMatch) {
    return normalizedDescription === normalizedQuery;
  } else {
    return normalizedDescription.includes(normalizedQuery);
  }
};

const VehicleSelector = ({items, onItemSelect, selectedItem, options}: VehicleSelectorProps) => {
  const {tenantPreferences} = usePreferenceContext();
  const {showSelectedText, iconSize, dropdownIcon, dropdownIconSize, scrollToActiveItem} = {
    ...DefaultOptions,
    ...options
  };
  const selectedVehicleDescription = items.find(
    (item) => item.vehicleTypeId === selectedItem?.vehicleTypeId
  )?.description;

  const itemRenderer: ItemRenderer<VehicleType> = useCallback(
    (vehicleType, {handleClick, handleFocus, modifiers}) => {
      if (!modifiers.matchesPredicate) {
        return null;
      }
      return (
        <MenuItem
          data-testid="vehicle-item"
          key={vehicleType.vehicleTypeId}
          active={modifiers.active}
          disabled={modifiers.disabled}
          onClick={handleClick}
          onFocus={handleFocus}
          roleStructure="listitem"
          icon={getVehicleIcon(vehicleType.description ?? "", tenantPreferences, iconSize)}
          text={vehicleType.description}
          labelElement={modifiers.active && <Icon icon="tick" />}
        />
      );
    },
    [iconSize, tenantPreferences]
  );

  return (
    <Select<VehicleType>
      items={items.toSorted((a, b) => (a.description || "").localeCompare(b.description || ""))}
      popoverProps={{
        minimal: true,
        popoverClassName: "vehicle-selector-popover",
        modifiers: DEFAULT_POPOVER_MODIFIERS
      }}
      itemRenderer={itemRenderer}
      itemPredicate={filterItem}
      onItemSelect={onItemSelect}
      activeItem={selectedItem}
      scrollToActiveItem={scrollToActiveItem}
      resetOnSelect
    >
      <StyledButton
        data-testid="vehicle-selector"
        variant="minimal"
        endIcon={<Icon size={dropdownIconSize} icon={dropdownIcon} />}
        icon={getVehicleIcon(selectedVehicleDescription ?? "", tenantPreferences, iconSize)}
        text={showSelectedText && (selectedVehicleDescription ?? EmptyValueStrings.unknown)}
      />
    </Select>
  );
};

export default memo(VehicleSelector);

const StyledButton = styled(Button)`
  border-radius: 6px;
  color: ${(props) => props.color ?? "#797979"} !important;
  padding: 0 4px;
  font-size: 14px;
  font-weight: 500;
  width: fit-content;
  min-width: 35px;
  display: flex;
  gap: 4px;
  justify-content: space-between;
`;
